// Shared profile-field shapes — kept in one place so Onboarding and Settings
// can't drift out of sync. Before this module they were defined twice and the
// Settings years_cutting input was a free-text box while Onboarding was a
// select with similar-but-different range strings. Now both screens render
// the same dropdown of 5 range buckets, and both run the same required-field
// check before persisting.

/** Stored value type. Five buckets from "0–3 years" to "15+ years." Stored
 *  as a text column to keep the existing nullable schema untouched. */
export type YearsCuttingValue = '0-3' | '4-7' | '8-11' | '12-14' | '15+';

export const YEARS_OPTIONS: ReadonlyArray<YearsCuttingValue> = [
  '0-3',
  '4-7',
  '8-11',
  '12-14',
  '15+',
];

/** Translation key per bucket. */
export function yearsLabelKey(value: YearsCuttingValue): string {
  switch (value) {
    case '0-3':   return 'profile.years.03';
    case '4-7':   return 'profile.years.47';
    case '8-11':  return 'profile.years.811';
    case '12-14': return 'profile.years.1214';
    case '15+':   return 'profile.years.15plus';
  }
}

/** Returns true if the stored value is one of the new canonical options.
 *  Used to detect rows still on the legacy range-string format ('lt1',
 *  '8-12', '13+') so the Settings dropdown can show the user that the
 *  field needs to be re-selected before save. */
export function isCanonicalYears(value: string | null | undefined): value is YearsCuttingValue {
  if (!value) return false;
  return (YEARS_OPTIONS as readonly string[]).includes(value);
}

/** Profile completeness check. The leaderboard only includes users who have
 *  the three required fields filled in correctly. Both the Onboarding step-1
 *  Continue button and the Settings Save button gate on this returning empty. */
export type ProfileRequirements = {
  first_name: string | null;
  zip: string | null;
  years_cutting: string | null;
};

export type MissingFields = Array<keyof ProfileRequirements>;

export function missingRequired(profile: ProfileRequirements): MissingFields {
  const missing: MissingFields = [];
  if (!profile.first_name || !profile.first_name.trim()) missing.push('first_name');
  if (!profile.zip || !/^\d{5}$/.test(profile.zip.trim())) missing.push('zip');
  if (!isCanonicalYears(profile.years_cutting)) missing.push('years_cutting');
  return missing;
}
